<template>
  <Dialog
    v-model:visible="debugStore.isDialogVisible"
    modal
    header="Document Access Debug"
    :style="{ width: '80vw', maxWidth: '1200px' }"
    :maximizable="true"
    class="debug-dialog"
  >
    <div class="debug-content">
      <div class="debug-header">
        <div class="flex justify-content-between align-items-center mb-3">
          <div>
            <h3 class="m-0">Document Access Operations</h3>
            <p class="text-500 m-0 mt-1">
              {{ debugStore.operations.length }} document access(es) recorded
            </p>
          </div>
          <div class="flex gap-2">
            <Button
              label="Clear All"
              icon="pi pi-trash"
              severity="danger"
              variant="outlined"
              size="small"
              @click="clearOperations"
            />
            <Button
              label="Copy All"
              icon="pi pi-copy"
              severity="secondary"
              variant="outlined"
              size="small"
              @click="copyAllOperations"
            />
          </div>
        </div>
      </div>

      <div class="debug-operations">
        <!-- Auth Persistence Debug Section -->
        <div class="auth-debug-section mb-4">
          <h4 class="mb-3">Auth Persistence Debug</h4>
          <div class="grid">
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Loading:</label>
                <div class="text-900">{{ authStore.loading }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Is Authenticated:</label>
                <div class="text-900">{{ authStore.isAuthenticated }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Has Persisted Auth:</label>
                <div class="text-900">{{ authStore.hasPersistedAuth }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Firebase User:</label>
                <div class="text-900">{{ authStore.firebaseUser ? 'Present' : 'None' }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Is SaaS Admin:</label>
                <div class="text-900">{{ authStore.isSaasAdmin }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Auth Tenant:</label>
                <div class="text-900">{{ authStore.authTenant || 'None' }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Current Tenant:</label>
                <div class="text-900">{{ authStore.currentTenant?.displayName || 'None' }}</div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field">
                <label class="font-semibold">Session Tenant ID:</label>
                <div class="text-900">{{ authStore.session.tenantId || 'None' }}</div>
              </div>
            </div>
          </div>

          <div class="field mt-3">
            <label class="font-semibold">Claims:</label>
            <div class="json-container">
              <pre class="json-content">{{ formatJson(authStore.claims) }}</pre>
            </div>
          </div>

          <div class="field mt-3">
            <label class="font-semibold">LocalStorage Data:</label>
            <div class="json-container">
              <pre class="json-content">{{ localStorageData }}</pre>
            </div>
          </div>

          <div class="flex gap-2 mt-3">
            <Button
              label="Clear Persisted Data"
              icon="pi pi-trash"
              severity="danger"
              variant="outlined"
              size="small"
              @click="clearPersistedData"
            />
            <Button
              label="Refresh Claims"
              icon="pi pi-refresh"
              severity="secondary"
              variant="outlined"
              size="small"
              @click="refreshClaims"
            />
          </div>
        </div>

        <!-- Document Operations Section -->
        <div class="operations-section">
          <h4 class="mb-3">Document Access Operations</h4>
          <div v-if="debugStore.operations.length === 0" class="text-center text-500 p-4">
            No document accesses recorded yet
          </div>

          <Accordion v-else :multiple="true" class="debug-accordion">
            <AccordionPanel
              v-for="operation in debugStore.operations"
              :key="operation.id"
              :value="operation.id"
              :header="getOperationHeader(operation)"
            >
              <div class="operation-header-actions mb-3">
                <div class="flex align-items-center gap-3">
                  <Tag
                    :value="operation.method.toUpperCase()"
                    :severity="getMethodSeverity(operation.method, operation.operation)"
                    class="method-tag"
                  />
                  <div class="text-sm text-500">
                    {{ formatTimestamp(operation.timestamp) }}
                  </div>
                </div>
                <div class="flex align-items-center gap-2">
                  <Button
                    icon="pi pi-copy"
                    size="small"
                    severity="secondary"
                    variant="text"
                    @click="copyOperation(operation)"
                    v-tooltip.top="'Copy JSON'"
                  />
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    variant="text"
                    @click="removeOperation(operation.id)"
                    v-tooltip.top="'Remove'"
                  />
                </div>
              </div>

              <div class="operation-details">
                <div class="grid">
                  <div class="col-12 md:col-6">
                    <div class="field">
                      <label class="font-semibold">Collection:</label>
                      <div class="text-900">{{ operation.collection }}</div>
                    </div>
                  </div>
                  <div class="col-12 md:col-6" v-if="operation.documentId">
                    <div class="field">
                      <label class="font-semibold">Document ID:</label>
                      <div class="text-900 font-mono text-sm">{{ operation.documentId }}</div>
                    </div>
                  </div>
                  <div class="col-12 md:col-6" v-if="operation.operation.includes('(')">
                    <div class="field">
                      <label class="font-semibold">Source:</label>
                      <div class="text-900">
                        <Tag
                          :value="getSourceFromOperation(operation.operation)"
                          :severity="getSourceSeverity(operation.operation)"
                          size="small"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="operation.data" class="field mt-3">
                  <label class="font-semibold">Data:</label>
                  <div class="json-container">
                    <pre class="json-content">{{ formatJson(operation.data) }}</pre>
                  </div>
                </div>
              </div>
            </AccordionPanel>
          </Accordion>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useDebugStore } from '@/stores/debugStore'
import { useAuthStore } from '@/stores/authStore'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import Tag from 'primevue/tag'
import { useToast } from 'primevue/usetoast'

const debugStore = useDebugStore()
const authStore = useAuthStore()
const toast = useToast()

const localStorageData = ref('')

onMounted(() => {
  updateLocalStorageData()
})

function updateLocalStorageData() {
  const authData = localStorage.getItem('auth')
  const userMgmtData = localStorage.getItem('userManagement')

  localStorageData.value = JSON.stringify(
    {
      auth: authData ? JSON.parse(authData) : null,
      userManagement: userMgmtData ? JSON.parse(userMgmtData) : null,
    },
    null,
    2,
  )
}

function clearPersistedData() {
  localStorage.removeItem('auth')
  localStorage.removeItem('userManagement')
  updateLocalStorageData()

  // Reset the store
  authStore.claims = {
    roles: [],
    authzVer: undefined,
    is_saas_admin: false,
    auth_tenant: null,
  }

  toast.add({
    severity: 'info',
    summary: 'Cleared',
    detail: 'Persisted data cleared! Refresh the page to see the effect.',
    life: 3000,
  })
}

function refreshClaims() {
  if (authStore.firebaseUser) {
    authStore.refreshClaims(authStore.firebaseUser)
    toast.add({
      severity: 'success',
      summary: 'Refreshed',
      detail: 'Claims refreshed successfully',
      life: 2000,
    })
  } else {
    toast.add({
      severity: 'warn',
      summary: 'No User',
      detail: 'No Firebase user available to refresh claims',
      life: 2000,
    })
  }
}

const getOperationHeader = (operation: any) => {
  return `${operation.method.toUpperCase()} - ${operation.collection}${operation.documentId ? ` (${operation.documentId})` : ''}`
}

const getMethodSeverity = (method: string, operation: string) => {
  // Check if it's a cache operation
  if (operation.includes('(cache)')) {
    return 'info'
  }

  const severityMap: Record<string, string> = {
    get: 'info',
    set: 'success',
    update: 'warning',
    delete: 'danger',
    add: 'success',
    list: 'info',
    query: 'info',
    count: 'info',
  }
  return severityMap[method] || 'secondary'
}

const getSourceFromOperation = (operation: string) => {
  if (operation.includes('(cache)')) return 'Cache'
  if (operation.includes('(firestore)')) return 'Firestore'
  if (operation.includes('(local)')) return 'Local'
  return 'Unknown'
}

const getSourceSeverity = (operation: string) => {
  if (operation.includes('(cache)')) return 'info'
  if (operation.includes('(firestore)')) return 'success'
  if (operation.includes('(local)')) return 'warning'
  return 'secondary'
}

const formatTimestamp = (timestamp: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(timestamp)
}

const formatJson = (data: unknown) => {
  return JSON.stringify(data, null, 2)
}

const copyOperation = async (operation: any) => {
  try {
    await navigator.clipboard.writeText(formatJson(operation))
    toast.add({
      severity: 'success',
      summary: 'Copied',
      detail: 'Document data copied to clipboard',
      life: 2000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to copy to clipboard',
      life: 3000,
    })
  }
}

const copyAllOperations = async () => {
  try {
    const allOperations = debugStore.operations.map((op) => ({
      id: op.id,
      timestamp: op.timestamp,
      operation: op.operation,
      collection: op.collection,
      documentId: op.documentId,
      data: op.data,
      method: op.method,
    }))
    await navigator.clipboard.writeText(formatJson(allOperations))
    toast.add({
      severity: 'success',
      summary: 'Copied',
      detail: 'All document accesses copied to clipboard',
      life: 2000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to copy to clipboard',
      life: 3000,
    })
  }
}

const clearOperations = () => {
  debugStore.clearOperations()
  toast.add({
    severity: 'info',
    summary: 'Cleared',
    detail: 'All document accesses cleared',
    life: 2000,
  })
}

const removeOperation = (id: string) => {
  const index = debugStore.operations.findIndex((op) => op.id === id)
  if (index > -1) {
    debugStore.operations.splice(index, 1)
    toast.add({
      severity: 'info',
      summary: 'Removed',
      detail: 'Document access removed',
      life: 2000,
    })
  }
}
</script>

<style scoped>
.debug-dialog :deep(.p-dialog-content) {
  padding: 0;
}

.debug-content {
  padding: 1.5rem;
}

.debug-header {
  border-bottom: 1px solid var(--surface-border);
  margin-bottom: 1rem;
}

.debug-operations {
  max-height: 60vh;
  overflow-y: auto;
}

.auth-debug-section {
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 1.5rem;
}

.operations-section {
  border-top: 1px solid var(--surface-border);
  padding-top: 1.5rem;
}

.debug-accordion :deep(.p-accordion-header) {
  padding: 0.75rem 1rem;
}

.debug-accordion :deep(.p-accordion-content) {
  padding: 1rem;
}

.method-tag {
  min-width: 60px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
}

.operation-details {
  background: var(--surface-50);
  border-radius: 6px;
  padding: 1rem;
}

.field {
  margin-bottom: 0.75rem;
}

.field:last-child {
  margin-bottom: 0;
}

.field label {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--text-color-secondary);
}

.json-container {
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  padding: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
}

.json-content {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--text-color);
  white-space: pre-wrap;
  word-break: break-all;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .debug-dialog {
    width: 95vw !important;
  }

  .debug-content {
    padding: 1rem;
  }

  .json-container {
    max-height: 200px;
  }
}
</style>
